import {
    TMcsaJourneyView,
    TMcsaWorldGamePhase,
    TMcsaWorldKey,
} from '../types/mcsa.interface';

export const MCSA_JOURNEY_BASE = '/mcsa/journey';

export const MCSA_WORLD_KEYS: TMcsaWorldKey[] = ['cur', 'travel', 'shop', 'hobby', 'mingle', 'online'];

export const isMcsaWorldKey = (value: string | undefined): value is TMcsaWorldKey => {
    return !!value && MCSA_WORLD_KEYS.includes(value as TMcsaWorldKey);
};

export interface IMcsaJourneyRoute {
    view: TMcsaJourneyView;
    worldKey: TMcsaWorldKey | null;
    questionIndex: number;
    gamePhase: TMcsaWorldGamePhase;
    gameIndex: number;
}

export const mcsaJourneyPath = {
    map: () => MCSA_JOURNEY_BASE,
    finish: () => `${MCSA_JOURNEY_BASE}/finish`,
    intro: (world: TMcsaWorldKey) => `${MCSA_JOURNEY_BASE}/${world}/intro`,
    question: (world: TMcsaWorldKey, index: number) => `${MCSA_JOURNEY_BASE}/${world}/question/${index}`,
    game: (world: TMcsaWorldKey, phase: TMcsaWorldGamePhase, index: number) => `${MCSA_JOURNEY_BASE}/${world}/game/${phase}/${index}`,
    preTransition: (world: TMcsaWorldKey) => `${MCSA_JOURNEY_BASE}/${world}/transition/pre`,
    postQuestionTransition: (world: TMcsaWorldKey) => `${MCSA_JOURNEY_BASE}/${world}/transition/post-q`,
    midGameTransition: (world: TMcsaWorldKey, index: number) => `${MCSA_JOURNEY_BASE}/${world}/transition/mid/${index}`,
    affirm: (world: TMcsaWorldKey) => `${MCSA_JOURNEY_BASE}/${world}/affirm`,
    wildcard: (world: TMcsaWorldKey) => `${MCSA_JOURNEY_BASE}/${world}/wildcard`,
};

export const parseMcsaJourneyPath = (pathname: string): IMcsaJourneyRoute => {
    const normalized = pathname.replace(/\/+$/, '') || '/';
    const prefix = MCSA_JOURNEY_BASE;
    const rest = normalized === prefix
        ? ''
        : normalized.startsWith(`${prefix}/`)
            ? normalized.slice(prefix.length + 1)
            : '';
    const parts = rest.split('/').filter(Boolean);

    const fallback: IMcsaJourneyRoute = {
        view: 'map',
        worldKey: null,
        questionIndex: 0,
        gamePhase: 'post',
        gameIndex: 0,
    };

    if (parts.length === 0) return fallback;
    if (parts[0] === 'finish') return { ...fallback, view: 'finish' };

    const worldKey = parts[0];
    if (!isMcsaWorldKey(worldKey)) return fallback;

    const step = parts[1];
    const asIndex = (value: string | undefined) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed >= 0 ? Math.floor(parsed) : 0;
    };

    if (!step || step === 'intro') return { ...fallback, view: 'intro', worldKey };
    if (step === 'question') return { ...fallback, view: 'question', worldKey, questionIndex: asIndex(parts[2]) };
    if (step === 'game') {
        const gamePhase: TMcsaWorldGamePhase = parts[2] === 'pre' ? 'pre' : 'post';
        return { ...fallback, view: 'game', worldKey, gamePhase, gameIndex: asIndex(parts[3]) };
    }
    if (step === 'transition') {
        const kind = parts[2];
        if (kind === 'pre') return { ...fallback, view: 'pre_game_transition', worldKey };
        if (kind === 'post-q') return { ...fallback, view: 'post_q_transition', worldKey };
        if (kind === 'mid') {
            return { ...fallback, view: 'mid_game_transition', worldKey, gamePhase: 'post', gameIndex: asIndex(parts[3]) };
        }
    }
    if (step === 'affirm') return { ...fallback, view: 'affirm', worldKey };
    if (step === 'wildcard') return { ...fallback, view: 'wildcard', worldKey };

    return { ...fallback, view: 'intro', worldKey };
};
